import React from "react";
import { Stack, StackProps, Text } from "@bookingcom/bui-react";
import { useUnifiedPriceTagController, type UnifiedPriceTagControllerProps } from "./useUnifiedPriceTag.controller";
import { UnifiedPriceTagPriceBreakdown } from "./UnifiedPriceTagPriceBreakdown";
import styles from "./UnifiedPriceTag.module.css";
import { isMobile } from "@bookingcom/mp-flights/build/cjs/constants";
import { mcn } from "utils/mergeClassnames";

export type UnifiedPriceTagProps = UnifiedPriceTagControllerProps & Pick<StackProps, "attributes">;

export const UnifiedPriceTag = (props: UnifiedPriceTagProps) => {
  const {
    formattedPrice,
    formattedStrikethroughPrice,
    priceWrapperStackProps,
    priceStackProps,
    priceTextProps,
    pricePerAdultExplanation,
    pricePerAdultExplanationStrikethroughInline
  } = useUnifiedPriceTagController(props);
  const {
    attributes,
    strikethroughPriceProps,
    priceExplanation,
    priceExplanationProps,
    pricePerAdultExplanationProps,
    shouldShowPricePerAdult
  } = props;

  const renderPricePerAdultExplanation = () => {
    if (!shouldShowPricePerAdult || !pricePerAdultExplanation) return;

    return (
      <Stack
        attributes={pricePerAdultExplanationProps?.attributes}
        className={pricePerAdultExplanationStrikethroughInline ? styles.inline : ""}
      >
        {formattedStrikethroughPrice && (
          <>
            <Text
              color="destructive"
              decoration="line-through"
              variant={
                isMobile && pricePerAdultExplanationProps?.variant ? pricePerAdultExplanationProps.variant : "small_1"
              }
              className={mcn(
                styles.strikedthroughPrice,
                pricePerAdultExplanationStrikethroughInline ? styles.inline : ""
              )}
            >
              {formattedStrikethroughPrice}
            </Text>
            <Text className={pricePerAdultExplanationStrikethroughInline ? styles.inline : ""}> </Text>
          </>
        )}
        <Text
          color="neutral_alt"
          variant={pricePerAdultExplanationProps?.variant || "body_2"}
          className={pricePerAdultExplanationStrikethroughInline ? styles.inline : ""}
          attributes={{ "data-testid": "upt_price_per_adult_explanation" }}
        >
          {pricePerAdultExplanation}
        </Text>
      </Stack>
    );
  };

  const renderExplanation = () => {
    if (shouldShowPricePerAdult || !priceExplanation) return;

    return (
      <Text variant="small_1" color="neutral_alt" attributes={priceExplanationProps?.attributes}>
        {priceExplanation}
      </Text>
    );
  };

  return (
    <Stack direction="column" gap={0} attributes={{ "data-testid": "upt_container" }}>
      <Stack direction="row" alignItems="center" gap={1} {...priceWrapperStackProps} attributes={attributes}>
        <Stack {...priceStackProps}>
          {formattedStrikethroughPrice && !shouldShowPricePerAdult && (
            <Text
              variant={strikethroughPriceProps?.variant || "body_2"}
              decoration="line-through"
              color="destructive"
              className={styles.strikedthroughPrice}
              attributes={{ "data-testid": "upt_price_strikethrough" }}
            >
              {formattedStrikethroughPrice}
            </Text>
          )}
          <Text className={styles.price} {...priceTextProps} attributes={{ "data-testid": "upt_price" }}>
            {formattedPrice}
          </Text>
        </Stack>
        <UnifiedPriceTagPriceBreakdown {...props} />
      </Stack>
      {renderPricePerAdultExplanation()}
      {renderExplanation()}
    </Stack>
  );
};
